import { useState } from 'react'
import { Routes, Route, useParams, useNavigate } from 'react-router-dom'
import './App.css'
import { StopsList } from './components/StopsList'
import { EtaView } from './components/EtaView'
import { StopInfoPage } from './components/StopInfoPage'
import { AreaPage } from './components/AreaPage'
import { AppLayout, useAppState } from './components/AppLayout'
import { HomePage } from './components/HomePage'
import { LinesPage as DedicatedLinesPage } from './components/LinesPage'
import { NearMePage } from './components/NearMePage'
import { PlanTripPage } from './components/PlanTripPage'
import { FavoritesBar } from './components/FavoritesBar'
import { useLines, useRoutes, useLineBySlug, useStops } from '@/lib/queries'
import { greekToLatin } from '@/lib/api'
import { useFavorites } from '@/lib/useFavorites'
import type { StopInfo, RouteInfo } from '@/lib/api'
import { DataError } from './components/DataError'

function getRouteDestination(route: RouteInfo | undefined, lang: 'el' | 'en'): string {
  if (!route) return ''
  const desc = lang === 'en'
    ? (route.RouteDescrEng || route.RouteDescr)
    : route.RouteDescr
  const match = desc.match(/^.+?\s*[-–]\s*(.+?)(?:\s*\[|$)/)
  return match ? match[1].trim() : desc
}

function StopsPageContent({
  selectedLine,
  selectedRoute,
  routes,
  lang,
  translations
}: {
  selectedLine: any
  selectedRoute: RouteInfo
  routes: RouteInfo[]
  lang: 'el' | 'en'
  translations: { showMap: string; showList: string }
}) {
  const navigate = useNavigate()
  const [selectedRouteCode, setSelectedRouteCode] = useState<string | null>(null)
  const currentRoute = routes?.find(r => r.RouteCode === selectedRouteCode) || selectedRoute
  const { data: stops, isLoading, isError, error, refetch } = useStops(currentRoute?.RouteCode)

  const selectStop = (stop: StopInfo) => {
    if (selectedLine && currentRoute) {
      const lineIdLatin = greekToLatin(selectedLine.LineID).toLowerCase()
      navigate(`/${lineIdLatin}/${currentRoute.RouteCode}/${stop.StopCode}`)
    }
  }

  if (isLoading) return <div className="loading">{lang === 'en' ? 'Loading...' : 'Φόρτωση...'}</div>
  if (isError) return <DataError error={error} lang={lang} onRetry={() => void refetch()} />
  if (!stops) return null

  return (
    <StopsList
      stops={stops}
      routes={routes}
      selectedStop={null}
      selectedLine={selectedLine}
      selectedRoute={currentRoute}
      onStopSelect={selectStop}
      onRouteSelect={(route: RouteInfo) => setSelectedRouteCode(route.RouteCode)}
      lang={lang}
      translations={translations}
    />
  )
}

function StopsPage() {
  const { lineSlug } = useParams<{ lineSlug: string }>()
  const { lang, t } = useAppState()

  const { data: lines } = useLines()
  const selectedLine = useLineBySlug(lines, lineSlug || null)

  const { data: routes, isLoading: routesLoading, isError: routesError, error, refetch } = useRoutes(selectedLine?.LineCode)
  const selectedRoute = routes?.[0]

  const isDataLoading = lineSlug && (routesLoading || !selectedRoute)

  if (routesError) {
    return <DataError error={error} lang={lang} onRetry={() => void refetch()} />
  }

  if (isDataLoading) {
    return <div className="loading">{t.loading}</div>
  }

  if (!selectedLine || !selectedRoute || !routes) return null

  return (
    <StopsPageContent
      selectedLine={selectedLine}
      selectedRoute={selectedRoute}
      routes={routes}
      lang={lang}
      translations={{ showMap: t.showMap, showList: t.showList }}
    />
  )
}

function EtaPage() {
  const { lineSlug, routeCode, stopCode } = useParams<{ lineSlug: string; routeCode: string; stopCode: string }>()
  const { lang, t } = useAppState()
  const { toggleFavorite, isFavorite } = useFavorites()

  const { data: lines } = useLines()
  const selectedLine = useLineBySlug(lines, lineSlug || null)

  const { data: routes, isLoading: routesLoading } = useRoutes(selectedLine?.LineCode)
  const selectedRoute = routes?.find(r => r.RouteCode === routeCode)

  const [selectedStop, setSelectedStop] = useState<StopInfo | null>(null)

  const isDataLoading = lineSlug && (routesLoading || !selectedRoute)

  const handleToggleFavorite = () => {
    if (!selectedLine || !selectedRoute || !selectedStop) return
    toggleFavorite({
      lineId: selectedLine.LineID,
      routeCode: selectedRoute.RouteCode,
      routeDescription: selectedRoute.RouteDescrEng || selectedRoute.RouteDescr,
      stopCode: selectedStop.StopCode,
      stopName: selectedStop.StopDescrEng || selectedStop.StopDescr
    })
  }

  const isCurrentFavorite = () => {
    if (!selectedLine || !selectedRoute || !selectedStop) return false
    return isFavorite(selectedLine.LineID, selectedRoute.RouteCode, selectedStop.StopCode)
  }

  if (isDataLoading) {
    return <div className="loading">{t.loading}</div>
  }

  if (!stopCode || !selectedRoute) return null

  return (
    <EtaView
      routeCode={selectedRoute.RouteCode}
      stopCode={stopCode}
      lineId={selectedLine?.LineID || ''}
      routeDestination={getRouteDestination(selectedRoute, lang)}
      isFavorite={isCurrentFavorite()}
      onToggleFavorite={handleToggleFavorite}
      onStopLoaded={setSelectedStop}
      lang={lang}
      translations={{
        loading: t.loading,
        min: t.min,
        stopsAway: t.stopsAway,
        bus: t.bus,
        nowAt: t.nowAt,
        noBuses: t.noBuses,
        passed: t.passed,
        busesPassed: t.busesPassed,
        otherBuses: t.otherBuses,
        stops: t.stops,
        refreshEta: t.refreshEta,
        official: t.official,
        late: t.late,
        early: t.early
      }}
    />
  )
}

function FavouritesPage() {
  const { lang } = useAppState()
  return <FavoritesBar lang={lang} />
}

function App() {
  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/favourites" element={<FavouritesPage />} />
        <Route path="/lines" element={<DedicatedLinesPage />} />
        <Route path="/near-me" element={<NearMePage />} />
        <Route path="/plan" element={<PlanTripPage />} />
        <Route path="/area" element={<AreaPage />} />
        <Route path="/area/:areaCode" element={<AreaPage />} />
        <Route path="/stop/:stopCode" element={<StopInfoPage />} />
        <Route path="/:lineSlug" element={<StopsPage />} />
        <Route path="/:lineSlug/:routeCode/:stopCode" element={<EtaPage />} />
      </Routes>
    </AppLayout>
  )
}

export default App
